/**
 * Sales Management Component
 * Full sales pipeline: Enquiry → Reservation → Deposit → Sale → Handover
 * Commission only applies if cattery is on 'full_platform' subscription
 */

import { useState, useEffect } from 'react';
import { 
  ShoppingCart,
  User,
  Phone,
  Mail,
  DollarSign,
  Calendar,
  CheckCircle,
  Clock,
  AlertTriangle,
  FileText,
  Plus,
  Edit,
  Trash2,
  Eye,
  MessageSquare,
  ArrowRight,
  Download,
  Send
} from 'lucide-react';
import { Card, Button, Badge, Modal, Input, Select, Alert, Tabs } from '../shared/UIComponents';
import { formatCurrency, formatDate, formatRelativeDate, cn } from '../../utils/helpers';
import { useAuth } from '../../hooks/useAuth';
import { getCatsByCattery } from '../../firebase/firestore';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where, 
  getDocs,
  getDoc,
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../firebase/config';

const SALE_STAGES = {
  enquiry: { label: 'Enquiry', color: 'bg-blue-100 text-blue-700', icon: MessageSquare },
  viewing: { label: 'Viewing Scheduled', color: 'bg-purple-100 text-purple-700', icon: Eye },
  reserved: { label: 'Reserved', color: 'bg-amber-100 text-amber-700', icon: Clock },
  deposit_paid: { label: 'Deposit Paid', color: 'bg-teal-100 text-teal-700', icon: DollarSign },
  ready_for_collection: { label: 'Ready for Collection', color: 'bg-indigo-100 text-indigo-700', icon: CheckCircle },
  completed: { label: 'Completed', color: 'bg-paw-100 text-paw-700', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-500', icon: AlertTriangle }
};

const COMMISSION_RATE = 0.10;

export const SalesManager = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sales, setSales] = useState([]);
  const [cats, setCats] = useState([]);
  const [kittens, setKittens] = useState([]);
  const [cattery, setCattery] = useState(null);
  const [showSaleModal, setShowSaleModal] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [activeTab, setActiveTab] = useState('pipeline');

  useEffect(() => {
    if (user?.catteryId) {
      loadData();
    }
  }, [user?.catteryId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load cattery to check subscription tier
      const catteryDoc = await getDoc(doc(db, 'catteries', user.catteryId));
      if (catteryDoc.exists()) {
        setCattery({ id: catteryDoc.id, ...catteryDoc.data() });
      }
      
      const catsData = await getCatsByCattery(user.catteryId);
      setCats(catsData);
      
      const kittensQuery = query(collection(db, 'kittens'), where('catteryId', '==', user.catteryId));
      const kittensSnap = await getDocs(kittensQuery);
      setKittens(kittensSnap.docs.map(doc => ({ id: doc.id, ...doc.data(), type: 'kitten' })));
      
      const salesQuery = query(collection(db, 'sales'), where('catteryId', '==', user.catteryId), orderBy('createdAt', 'desc'));
      const salesSnap = await getDocs(salesQuery);
      setSales(salesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error loading sales data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStageChange = async (saleId, newStage) => {
    try {
      const saleRef = doc(db, 'sales', saleId);
      const updateData = { stage: newStage, updatedAt: serverTimestamp() };

      if (newStage === 'completed') {
        const saleDoc = await getDoc(saleRef);
        if (saleDoc.exists()) {
          const saleData = saleDoc.data();
          
          // Only create commission entry if cattery is on Full Platform tier
          // Registry Only users don't pay sales commission - they only pay registry fees
          const isFullPlatform = cattery?.subscriptionTier !== 'registry_only';
          
          if (isFullPlatform) {
            await addDoc(collection(db, 'commission_ledger'), {
              breederId: user.uid,
              catteryId: user.catteryId,
              type: 'sale_commission',
              saleId: saleId,
              saleAmount: saleData.price,
              commissionAmount: Math.round(saleData.price * COMMISSION_RATE),
              commissionRate: COMMISSION_RATE,
              status: 'pending',
              month: new Date().toLocaleString('en-ZA', { month: 'short', year: 'numeric' }),
              dueDate: Timestamp.fromDate(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 7)),
              createdAt: serverTimestamp()
            });
          }

          if (saleData.catId) {
            await updateDoc(doc(db, 'cats', saleData.catId), { status: 'sold', updatedAt: serverTimestamp() });
          }
          if (saleData.kittenId) {
            await updateDoc(doc(db, 'kittens', saleData.kittenId), { status: 'sold', updatedAt: serverTimestamp() });
          }
          updateData.completedAt = serverTimestamp();
        }
      }

      await updateDoc(saleRef, updateData);
      loadData();
    } catch (error) {
      console.error('Error updating sale stage:', error);
    }
  };

  const availableForSale = [
    ...cats.filter(c => c.status === 'available').map(c => ({ ...c, itemType: 'cat' })),
    ...kittens.filter(k => k.status === 'available').map(k => ({ ...k, itemType: 'kitten' }))
  ];

  const pipelineSales = sales.filter(s => !['completed', 'cancelled'].includes(s.stage));
  const completedSales = sales.filter(s => s.stage === 'completed');

  // Check if user is on full platform (pays commission) or registration only (no commission)
  const isFullPlatform = user?.subscriptionTier !== 'registration_only';

  const stats = {
    pipeline: pipelineSales.length,
    pipelineValue: pipelineSales.reduce((sum, s) => sum + (s.price || 0), 0),
    completed: completedSales.length,
    completedValue: completedSales.reduce((sum, s) => sum + (s.price || 0), 0),
    depositsHeld: pipelineSales.reduce((sum, s) => sum + (s.depositPaid || 0), 0)
  };

  const tabs = [
    { id: 'pipeline', label: 'Sales Pipeline', count: pipelineSales.length },
    { id: 'completed', label: 'Completed', count: completedSales.length },
    { id: 'all', label: 'All Sales' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-gray-900">Sales Management</h1>
          <p className="text-gray-500">Track sales from enquiry to handover</p>
        </div>
        <Button onClick={() => setShowSaleModal(true)}>
          <Plus className="w-4 h-4" />
          New Sale
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <p className="text-sm text-white/80">Pipeline</p>
          <p className="text-3xl font-bold">{stats.pipeline}</p>
          <p className="text-sm text-white/80 mt-1">{formatCurrency(stats.pipelineValue)} value</p>
        </Card>
        <Card className="bg-gradient-to-br from-paw-500 to-paw-600 text-white">
          <p className="text-sm text-white/80">Completed Sales</p>
          <p className="text-3xl font-bold">{stats.completed}</p>
          <p className="text-sm text-white/80 mt-1">{formatCurrency(stats.completedValue)} total</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Deposits Held</p>
          <p className="text-3xl font-bold text-amber-600">{formatCurrency(stats.depositsHeld)}</p>
        </Card>
        {isFullPlatform ? (
          <Card>
            <p className="text-sm text-gray-500">Commission Due (10%)</p>
            <p className="text-3xl font-bold text-feline-600">{formatCurrency(stats.completedValue * COMMISSION_RATE)}</p>
          </Card>
        ) : (
          <Card className="bg-gray-50">
            <p className="text-sm text-gray-500">Commission</p>
            <p className="text-xl font-bold text-gray-400">N/A</p>
            <p className="text-xs text-gray-400">Registration Only plan</p>
          </Card>
        )}
      </div>

      {availableForSale.length > 0 && (
        <Alert type="info">
          <p className="text-sm"><strong>{availableForSale.length}</strong> cats/kittens available for sale.</p>
        </Alert>
      )}

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === 'pipeline' ? (
        <SalesPipeline 
          sales={pipelineSales}
          loading={loading}
          cats={cats}
          kittens={kittens}
          onEdit={(sale) => { setSelectedSale(sale); setShowSaleModal(true); }}
          onStageChange={handleStageChange}
        />
      ) : (
        <SalesList 
          sales={activeTab === 'completed' ? completedSales : sales}
          loading={loading}
          cats={cats}
          kittens={kittens}
          onEdit={(sale) => { setSelectedSale(sale); setShowSaleModal(true); }}
        />
      )}

      <SaleModal
        isOpen={showSaleModal}
        onClose={() => { setShowSaleModal(false); setSelectedSale(null); }}
        sale={selectedSale}
        availableItems={availableForSale}
        cats={cats}
        kittens={kittens}
        catteryId={user?.catteryId}
        onSuccess={() => { setShowSaleModal(false); setSelectedSale(null); loadData(); }}
      />
    </div>
  );
};

const SalesPipeline = ({ sales, loading, cats, kittens, onEdit, onStageChange }) => {
  const stages = ['enquiry', 'viewing', 'reserved', 'deposit_paid', 'ready_for_collection'];
  
  const getItem = (sale) => {
    if (sale.catId) return cats.find(c => c.id === sale.catId);
    if (sale.kittenId) return kittens.find(k => k.id === sale.kittenId);
    return null;
  };

  if (loading) {
    return <div className="grid grid-cols-5 gap-4">
      {stages.map(s => <Card key={s} className="animate-pulse"><div className="h-48 bg-gray-100 rounded" /></Card>)}
    </div>;
  }

  return (
    <div className="overflow-x-auto pb-4">
      <div className="flex gap-4 min-w-max">
        {stages.map((stage) => {
          const stageSales = sales.filter(s => s.stage === stage);
          const stageConfig = SALE_STAGES[stage];
          
          return (
            <div key={stage} className="w-72 flex-shrink-0">
              <div className={cn('px-4 py-2 rounded-t-lg font-medium text-sm flex items-center justify-between', stageConfig.color)}>
                <span>{stageConfig.label}</span>
                <Badge variant="secondary">{stageSales.length}</Badge>
              </div>
              
              <div className="bg-gray-100 rounded-b-lg p-2 min-h-[400px] space-y-2">
                {stageSales.map((sale) => (
                  <PipelineCard
                    key={sale.id}
                    sale={sale}
                    item={getItem(sale)}
                    stage={stage}
                    stages={stages}
                    onEdit={() => onEdit(sale)}
                    onStageChange={onStageChange}
                  />
                ))}
                {stageSales.length === 0 && (
                  <p className="text-center text-gray-400 text-sm py-8">No sales</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PipelineCard = ({ sale, item, stage, stages, onEdit, onStageChange }) => {
  const currentIndex = stages.indexOf(stage);
  const nextStage = stages[currentIndex + 1];

  return (
    <Card className="!p-3 hover:shadow-md transition-shadow">
      <div className="flex items-center gap-2 mb-2" onClick={onEdit}>
        <div className="w-10 h-10 bg-gray-200 rounded-lg overflow-hidden cursor-pointer">
          {item?.photos?.[0] ? (
            <img src={item.photos[0]} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-lg">🐱</div>
          )}
        </div>
        <div className="flex-1 min-w-0 cursor-pointer">
          <p className="font-medium text-gray-900 truncate">{item?.name || sale.itemName || 'Unknown'}</p>
          <p className="text-xs text-gray-500">{item?.breed || sale.breed}</p>
        </div>
      </div>
      
      <div className="text-sm mb-2" onClick={onEdit}>
        <p className="flex items-center gap-1 text-gray-600 cursor-pointer">
          <User className="w-3 h-3" />{sale.buyerName}
        </p>
        {sale.buyerPhone && (
          <p className="flex items-center gap-1 text-gray-500 text-xs">
            <Phone className="w-3 h-3" />{sale.buyerPhone}
          </p>
        )}
      </div>
      
      <div className="flex items-center justify-between text-sm mb-3">
        <span className="font-bold text-feline-600">{formatCurrency(sale.price)}</span>
        {sale.depositPaid > 0 && (
          <Badge variant="success" className="text-xs">R{sale.depositPaid} dep</Badge>
        )}
      </div>

      {/* Stage advancement buttons */}
      <div className="flex gap-2">
        {nextStage && (
          <Button 
            size="sm" 
            className="flex-1 text-xs"
            onClick={() => onStageChange(sale.id, nextStage)}
          >
            <ArrowRight className="w-3 h-3" />
            {SALE_STAGES[nextStage].label}
          </Button>
        )}
        {stage === 'ready_for_collection' && (
          <Button 
            size="sm" 
            variant="primary"
            className="flex-1 text-xs bg-paw-500 hover:bg-paw-600"
            onClick={() => onStageChange(sale.id, 'completed')}
          >
            <CheckCircle className="w-3 h-3" />
            Complete Sale
          </Button>
        )}
      </div>
    </Card>
  );
};

const SalesList = ({ sales, loading, cats, kittens, onEdit }) => {
  const getItem = (sale) => {
    if (sale.catId) return cats.find(c => c.id === sale.catId);
    if (sale.kittenId) return kittens.find(k => k.id === sale.kittenId);
    return null;
  };

  if (loading) {
    return <Card><div className="animate-pulse space-y-4">
      {[...Array(5)].map((_, i) => <div key={i} className="h-16 bg-gray-100 rounded-lg" />)}
    </div></Card>;
  }

  if (sales.length === 0) {
    return (
      <Card className="text-center py-12">
        <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500">No sales recorded yet</p>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden p-0">
      <div className="divide-y divide-gray-200">
        {sales.map((sale) => {
          const item = getItem(sale);
          const stageConfig = SALE_STAGES[sale.stage];
          
          return (
            <div 
              key={sale.id} 
              className="p-4 hover:bg-gray-50 transition-colors flex items-center gap-4 cursor-pointer"
              onClick={() => onEdit(sale)}
            >
              <div className="w-12 h-12 bg-gray-200 rounded-lg overflow-hidden">
                {item?.photos?.[0] ? (
                  <img src={item.photos[0]} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xl">🐱</div>
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900">{item?.name || sale.itemName}</p>
                <p className="text-sm text-gray-500">{sale.buyerName} • {sale.buyerPhone}</p>
              </div>
              
              <div className="text-right">
                <p className="font-bold text-gray-900">{formatCurrency(sale.price)}</p>
                <p className="text-xs text-gray-500">{formatDate(sale.createdAt)}</p>
              </div>
              
              <Badge className={stageConfig?.color}>{stageConfig?.label}</Badge>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

const SaleModal = ({ isOpen, onClose, sale, availableItems, cats, kittens, catteryId, onSuccess }) => {
  const [formData, setFormData] = useState({
    itemId: '',
    itemType: '',
    itemName: '',
    breed: '',
    price: '',
    depositAmount: '',
    depositPaid: '',
    stage: 'enquiry',
    buyerName: '',
    buyerEmail: '',
    buyerPhone: '',
    buyerAddress: '',
    viewingDate: '',
    collectionDate: '',
    notes: '',
    contractSigned: false
  });
  const [submitting, setSubmitting] = useState(false);

  const isEditing = !!sale;

  useEffect(() => {
    if (sale) {
      setFormData({
        itemId: sale.catId || sale.kittenId || '',
        itemType: sale.catId ? 'cat' : 'kitten',
        itemName: sale.itemName || '',
        breed: sale.breed || '',
        price: sale.price || '',
        depositAmount: sale.depositAmount || '',
        depositPaid: sale.depositPaid || '',
        stage: sale.stage || 'enquiry',
        buyerName: sale.buyerName || '',
        buyerEmail: sale.buyerEmail || '',
        buyerPhone: sale.buyerPhone || '',
        buyerAddress: sale.buyerAddress || '',
        viewingDate: sale.viewingDate ? formatDateForInput(sale.viewingDate) : '',
        collectionDate: sale.collectionDate ? formatDateForInput(sale.collectionDate) : '',
        notes: sale.notes || '',
        contractSigned: sale.contractSigned || false
      });
    } else {
      setFormData({
        itemId: '', itemType: '', itemName: '', breed: '', price: '', depositAmount: '', depositPaid: '',
        stage: 'enquiry', buyerName: '', buyerEmail: '', buyerPhone: '', buyerAddress: '',
        viewingDate: '', collectionDate: '', notes: '', contractSigned: false
      });
    }
  }, [sale]);

  const formatDateForInput = (date) => {
    const d = date?.toDate ? date.toDate() : new Date(date);
    return d.toISOString().split('T')[0];
  };

  const handleItemSelect = (itemId) => {
    const item = availableItems.find(i => i.id === itemId);
    if (item) {
      setFormData({
        ...formData,
        itemId: item.id,
        itemType: item.itemType,
        itemName: item.name,
        breed: item.breed,
        price: item.price || ''
      });
    }
  };

  const handleSubmit = async () => {
    if (!formData.buyerName || !formData.price) return;
    try {
      setSubmitting(true);
      
      const data = {
        catId: formData.itemType === 'cat' ? formData.itemId : null,
        kittenId: formData.itemType === 'kitten' ? formData.itemId : null,
        itemName: formData.itemName,
        breed: formData.breed,
        price: Number(formData.price),
        depositAmount: formData.depositAmount ? Number(formData.depositAmount) : null,
        depositPaid: formData.depositPaid ? Number(formData.depositPaid) : 0,
        stage: formData.stage,
        buyerName: formData.buyerName,
        buyerEmail: formData.buyerEmail,
        buyerPhone: formData.buyerPhone,
        buyerAddress: formData.buyerAddress,
        viewingDate: formData.viewingDate ? Timestamp.fromDate(new Date(formData.viewingDate)) : null,
        collectionDate: formData.collectionDate ? Timestamp.fromDate(new Date(formData.collectionDate)) : null,
        notes: formData.notes,
        contractSigned: formData.contractSigned,
        catteryId,
        updatedAt: serverTimestamp()
      };

      if (isEditing) {
        await updateDoc(doc(db, 'sales', sale.id), data);
      } else {
        data.createdAt = serverTimestamp();
        await addDoc(collection(db, 'sales'), data);
        
        // Update cat/kitten status to reserved
        if (formData.itemId) {
          const collection_name = formData.itemType === 'cat' ? 'cats' : 'kittens';
          await updateDoc(doc(db, collection_name, formData.itemId), { 
            status: 'reserved',
            updatedAt: serverTimestamp()
          });
        }
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving sale:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const itemOptions = availableItems.map(i => ({
    value: i.id,
    label: `${i.name} (${i.itemType}) - ${i.breed}`
  }));

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEditing ? 'Edit Sale' : 'New Sale'} size="lg">
      <div className="space-y-4 max-h-[70vh] overflow-y-auto">
        {/* Item Selection */}
        {!isEditing && (
          <Select
            label="Cat/Kitten"
            value={formData.itemId}
            onChange={(e) => handleItemSelect(e.target.value)}
            options={itemOptions}
            placeholder="Select cat or kitten"
          />
        )}

        {isEditing && (
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="font-medium text-gray-900">{formData.itemName}</p>
            <p className="text-sm text-gray-500">{formData.breed}</p>
          </div>
        )}

        {/* Buyer Info */}
        <div className="pt-4 border-t border-gray-200">
          <h4 className="font-medium text-gray-900 mb-3">Buyer Information</h4>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Name *" value={formData.buyerName} onChange={(e) => setFormData({ ...formData, buyerName: e.target.value })} placeholder="Full name" />
            <Input label="Phone *" value={formData.buyerPhone} onChange={(e) => setFormData({ ...formData, buyerPhone: e.target.value })} placeholder="082 123 4567" />
            <Input label="Email" value={formData.buyerEmail} onChange={(e) => setFormData({ ...formData, buyerEmail: e.target.value })} placeholder="email@example.com" />
            <Input label="Address" value={formData.buyerAddress} onChange={(e) => setFormData({ ...formData, buyerAddress: e.target.value })} placeholder="City, Province" />
          </div>
        </div>

        {/* Sale Details */}
        <div className="pt-4 border-t border-gray-200">
          <h4 className="font-medium text-gray-900 mb-3">Sale Details</h4>
          <div className="grid grid-cols-3 gap-4">
            <Input label="Sale Price (ZAR) *" type="number" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} placeholder="0" />
            <Input label="Deposit Required" type="number" value={formData.depositAmount} onChange={(e) => setFormData({ ...formData, depositAmount: e.target.value })} placeholder="0" />
            <Input label="Deposit Paid" type="number" value={formData.depositPaid} onChange={(e) => setFormData({ ...formData, depositPaid: e.target.value })} placeholder="0" />
          </div>
          
          {formData.price && (
            <div className="mt-3 p-3 bg-amber-50 rounded-lg">
              <p className="text-sm text-amber-800">
                Commission on this sale: <strong>{formatCurrency(Number(formData.price) * COMMISSION_RATE)}</strong> (10%)
              </p>
            </div>
          )}
        </div>

        {/* Status & Dates */}
        <div className="pt-4 border-t border-gray-200">
          <h4 className="font-medium text-gray-900 mb-3">Status & Dates</h4>
          <div className="grid grid-cols-3 gap-4">
            <Select
              label="Stage"
              value={formData.stage}
              onChange={(e) => setFormData({ ...formData, stage: e.target.value })}
              options={Object.entries(SALE_STAGES).map(([key, val]) => ({ value: key, label: val.label }))}
            />
            <Input label="Viewing Date" type="date" value={formData.viewingDate} onChange={(e) => setFormData({ ...formData, viewingDate: e.target.value })} />
            <Input label="Collection Date" type="date" value={formData.collectionDate} onChange={(e) => setFormData({ ...formData, collectionDate: e.target.value })} />
          </div>
        </div>

        {/* Contract */}
        <div className="pt-4 border-t border-gray-200">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.contractSigned}
              onChange={(e) => setFormData({ ...formData, contractSigned: e.target.checked })}
              className="w-5 h-5 text-feline-500 rounded border-gray-300"
            />
            <span className="text-gray-700">Sale contract signed</span>
          </label>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={3}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-feline-300 focus:border-feline-500 outline-none"
            placeholder="Any additional notes about this sale..."
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} loading={submitting}>
            {isEditing ? 'Update' : 'Create'} Sale
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default SalesManager;
